import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FileText, Plus, Search, Edit, Trash2, Eye, X, TrendingUp, TrendingDown, DollarSign, BarChart3 } from 'lucide-react';

interface FinancialReport {
  id: number;
  title: string;
  description?: string;
  reportType: string;
  startDate: string;
  endDate: string;
  totalAmount: number;
  previousPeriodAmount: number;
  growthPercentage: number;
  currency: string;
  fileUrl?: string;
  status: string;
  createdAt: string;
}

const T = {
  title: "التقارير المالية",
  subtitle: "إدارة وعرض التقارير المالية للشركة",
  createNew: "إنشاء تقرير جديد",
  totalReports: "إجمالي التقارير",
  totalRevenue: "إجمالي الإيرادات",
  totalExpenses: "إجمالي المصروفات",
  netProfit: "صافي الربح",
  search: "بحث بعنوان التقرير...",
  loading: "جاري التحميل...",
  noReports: "لا توجد تقارير مالية",
  thTitle: "عنوان التقرير",
  thType: "النوع",
  thPeriod: "الفترة",
  thAmount: "المبلغ",
  thGrowth: "النمو",
  thStatus: "الحالة",
  thActions: "الإجراءات",
  view: "عرض",
  edit: "تعديل",
  delete: "حذف",
  editReport: "تعديل التقرير المالي",
  createReport: "إنشاء تقرير مالي جديد",
  viewReport: "تفاصيل التقرير المالي",
  lblTitle: "عنوان التقرير",
  lblDesc: "الوصف",
  lblType: "نوع التقرير",
  lblStatus: "الحالة",
  lblStartDate: "تاريخ البداية",
  lblEndDate: "تاريخ النهاية",
  lblAmount: "المبلغ الإجمالي",
  lblPrevAmount: "مبلغ الفترة السابقة",
  lblGrowth: "نسبة النمو (%)",
  lblCurrency: "العملة",
  cancel: "إلغاء",
  update: "تحديث",
  create: "إنشاء",
  close: "إغلاق",
  confirmDelete: "هل أنت متأكد من حذف التقرير؟",
  updated: "تم تحديث التقرير بنجاح",
  created: "تم إنشاء التقرير بنجاح",
  deleted: "تم الحذف بنجاح",
  error: "حدث خطأ",
  revenue: "إيرادات",
  expense: "مصروفات",
  profit: "أرباح",
  published: "منشور",
  draft: "مسودة",
  archived: "مؤرشف",
  egp: "جنيه مصري",
  usd: "دولار أمريكي",
  eur: "يورو"
};

export default function FinancialReportsPage() {
  const [reports, setReports] = useState<FinancialReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingReport, setEditingReport] = useState<FinancialReport | null>(null);
  const [selectedReport, setSelectedReport] = useState<FinancialReport | null>(null);
  const [form, setForm] = useState({
    title: '', description: '', reportType: 'Revenue',
    startDate: '', endDate: '', totalAmount: '0',
    previousPeriodAmount: '0', growthPercentage: '0',
    currency: 'EGP', status: 'Draft'
  });

  useEffect(() => { loadReports(); }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/financialreports');
      if (res.data?.success) {
        const data = res.data.data;
        setReports(Array.isArray(data) ? data : (data.items || []));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingReport(null);
    setForm({ title: '', description: '', reportType: 'Revenue', startDate: '', endDate: '', totalAmount: '0', previousPeriodAmount: '0', growthPercentage: '0', currency: 'EGP', status: 'Draft' });
    setShowModal(true);
  };

  const openEdit = (r: FinancialReport) => {
    setEditingReport(r);
    setForm({
      title: r.title || '',
      description: r.description || '',
      reportType: r.reportType || 'Revenue',
      startDate: r.startDate ? r.startDate.split('T')[0] : '',
      endDate: r.endDate ? r.endDate.split('T')[0] : '',
      totalAmount: r.totalAmount.toString(),
      previousPeriodAmount: r.previousPeriodAmount.toString(),
      growthPercentage: r.growthPercentage.toString(),
      currency: r.currency || 'EGP',
      status: r.status || 'Draft'
    });
    setShowModal(true);
  };

  const openView = (r: FinancialReport) => { setSelectedReport(r); setShowViewModal(true); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        title: form.title,
        description: form.description,
        reportType: form.reportType,
        startDate: form.startDate,
        endDate: form.endDate,
        totalAmount: parseFloat(form.totalAmount) || 0,
        previousPeriodAmount: parseFloat(form.previousPeriodAmount) || 0,
        growthPercentage: parseFloat(form.growthPercentage) || 0,
        currency: form.currency,
        status: form.status
      };
      if (editingReport) {
        await api.put('/api/financialreports/' + editingReport.id, payload);
        alert(T.updated);
      } else {
        await api.post('/api/financialreports', payload);
        alert(T.created);
      }
      setShowModal(false);
      loadReports();
    } catch (err: any) {
      alert(err.response?.data?.message || T.error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(T.confirmDelete)) return;
    try {
      await api.delete('/api/financialreports/' + id);
      loadReports();
      alert(T.deleted);
    } catch (err: any) {
      alert(err.response?.data?.message || T.error);
    }
  };

  const filtered = reports.filter(r =>
    (r.title || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.reportType || '').toLowerCase().includes(search.toLowerCase())
  );

  const totalRevenue = reports.filter(r => r.reportType === 'Revenue').reduce((sum, r) => sum + r.totalAmount, 0);
  const totalExpenses = reports.filter(r => r.reportType === 'Expense').reduce((sum, r) => sum + r.totalAmount, 0);
  const netProfit = totalRevenue - totalExpenses;
  const inputClass = 'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition';

  const getReportTypeColor = (type: string) => {
    if (type === 'Revenue') return 'bg-green-50 text-green-700 border-green-200';
    if (type === 'Expense') return 'bg-red-50 text-red-700 border-red-200';
    if (type === 'Profit') return 'bg-blue-50 text-blue-700 border-blue-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const getStatusColor = (status: string) => {
    if (status === 'Published') return 'bg-green-50 text-green-700';
    if (status === 'Draft') return 'bg-yellow-50 text-yellow-700';
    if (status === 'Archived') return 'bg-gray-100 text-gray-600';
    return 'bg-gray-50 text-gray-700';
  };

  const getTypeLabel = (type: string) => {
    if (type === 'Revenue') return T.revenue;
    if (type === 'Expense') return T.expense;
    if (type === 'Profit') return T.profit;
    return type;
  };

  const getStatusLabel = (status: string) => {
    if (status === 'Published') return T.published;
    if (status === 'Draft') return T.draft;
    if (status === 'Archived') return T.archived;
    return status;
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <span>{T.title}</span>
            <p className="text-sm text-gray-500 font-normal mt-1">{T.subtitle}</p>
          </div>
        </h1>
        <Button onClick={openCreate} className="bg-gradient-to-l from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg">
          <Plus className="w-5 h-5 ml-2" />
          <span>{T.createNew}</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center"><FileText className="w-5 h-5 text-indigo-600" /></div></div>
          <p className="text-sm text-gray-500">{T.totalReports}</p>
          <p className="text-2xl font-bold text-gray-900">{reports.length}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-green-600" /></div></div>
          <p className="text-sm text-gray-500">{T.totalRevenue}</p>
          <p className="text-2xl font-bold text-green-700">{totalRevenue.toLocaleString()} EGP</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center"><TrendingDown className="w-5 h-5 text-red-600" /></div></div>
          <p className="text-sm text-gray-500">{T.totalExpenses}</p>
          <p className="text-2xl font-bold text-red-700">{totalExpenses.toLocaleString()} EGP</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><DollarSign className="w-5 h-5 text-blue-600" /></div></div>
          <p className="text-sm text-gray-500">{T.netProfit}</p>
          <p className={"text-2xl font-bold " + (netProfit >= 0 ? 'text-blue-700' : 'text-red-700')}>{netProfit.toLocaleString()} EGP</p>
        </CardContent></Card>
      </div>

      <Card className="border-0 shadow-lg">
        <div className="p-6">
          <div className="relative mb-6">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder={T.search} value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition" />
          </div>
          {loading ? (
            <div className="p-12 text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div><p className="text-gray-500 mt-4">{T.loading}</p></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center"><FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" /><h3 className="text-lg font-semibold text-gray-900 mb-2">{T.noReports}</h3></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thTitle}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thType}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thPeriod}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thAmount}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thGrowth}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thStatus}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thActions}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((report) => (
                    <tr key={report.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center text-white"><FileText className="w-5 h-5" /></div>
                          <div>
                            <p className="font-semibold text-gray-900">{report.title}</p>
                            <p className="text-xs text-gray-500">{report.description ? report.description.substring(0, 50) : '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-5"><span className={"px-3 py-1 rounded-full text-xs font-semibold border " + getReportTypeColor(report.reportType)}>{getTypeLabel(report.reportType)}</span></td>
                      <td className="py-4 px-5"><span className="text-sm text-gray-700">{new Date(report.startDate).toLocaleDateString('ar-EG')} - {new Date(report.endDate).toLocaleDateString('ar-EG')}</span></td>
                      <td className="py-4 px-5"><span className="font-bold text-gray-900">{report.totalAmount.toLocaleString()} {report.currency}</span></td>
                      <td className="py-4 px-5">
                        <span className={"inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold " + (report.growthPercentage >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700')}>
                          {report.growthPercentage >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {report.growthPercentage}%
                        </span>
                      </td>
                      <td className="py-4 px-5"><span className={"px-3 py-1 rounded-full text-xs font-semibold " + getStatusColor(report.status)}>{getStatusLabel(report.status)}</span></td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openView(report)} className="p-2 hover:bg-indigo-50 rounded-lg text-gray-500 hover:text-indigo-600 transition" title={T.view}><Eye className="w-4 h-4" /></button>
                          <button onClick={() => openEdit(report)} className="p-2 hover:bg-indigo-50 rounded-lg text-gray-500 hover:text-indigo-600 transition" title={T.edit}><Edit className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete(report.id)} className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition" title={T.delete}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{editingReport ? T.editReport : T.createReport}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2"><Label>{T.lblTitle} *</Label><Input value={form.title} onChange={(e: any) => setForm({...form, title: e.target.value})} required className={inputClass} /></div>
                <div className="md:col-span-2"><Label>{T.lblDesc}</Label><Input value={form.description} onChange={(e: any) => setForm({...form, description: e.target.value})} className={inputClass} /></div>
                <div><Label>{T.lblType}</Label><select value={form.reportType} onChange={(e) => setForm({...form, reportType: e.target.value})} className={inputClass}><option value="Revenue">{T.revenue}</option><option value="Expense">{T.expense}</option><option value="Profit">{T.profit}</option></select></div>
                <div><Label>{T.lblStatus}</Label><select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className={inputClass}><option value="Draft">{T.draft}</option><option value="Published">{T.published}</option><option value="Archived">{T.archived}</option></select></div>
                <div><Label>{T.lblStartDate}</Label><Input type="date" value={form.startDate} onChange={(e: any) => setForm({...form, startDate: e.target.value})} className={inputClass} /></div>
                <div><Label>{T.lblEndDate}</Label><Input type="date" value={form.endDate} onChange={(e: any) => setForm({...form, endDate: e.target.value})} className={inputClass} /></div>
                <div><Label>{T.lblAmount}</Label><Input type="number" step="0.01" value={form.totalAmount} onChange={(e: any) => setForm({...form, totalAmount: e.target.value})} className={inputClass} /></div>
                <div><Label>{T.lblPrevAmount}</Label><Input type="number" step="0.01" value={form.previousPeriodAmount} onChange={(e: any) => setForm({...form, previousPeriodAmount: e.target.value})} className={inputClass} /></div>
                <div><Label>{T.lblGrowth}</Label><Input type="number" step="0.01" value={form.growthPercentage} onChange={(e: any) => setForm({...form, growthPercentage: e.target.value})} className={inputClass} /></div>
                <div><Label>{T.lblCurrency}</Label><select value={form.currency} onChange={(e) => setForm({...form, currency: e.target.value})} className={inputClass}><option value="EGP">{T.egp}</option><option value="USD">{T.usd}</option><option value="EUR">{T.eur}</option></select></div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">{T.cancel}</Button>
                <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700">{editingReport ? T.update : T.create}</Button>
              </div>
            </form>
          </div></Card>
        </div>
      )}

      {showViewModal && selectedReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{T.viewReport}</h2>
              <button onClick={() => setShowViewModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex items-center gap-4 pb-4 border-b mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center text-white"><FileText className="w-8 h-8" /></div>
              <div><h3 className="text-xl font-bold">{selectedReport.title}</h3><p className="text-gray-500">{selectedReport.description || '-'}</p></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-sm text-gray-500">{T.thType}</p><span className={"px-3 py-1 rounded-full text-xs font-semibold border " + getReportTypeColor(selectedReport.reportType)}>{getTypeLabel(selectedReport.reportType)}</span></div>
              <div><p className="text-sm text-gray-500">{T.thStatus}</p><span className={"px-3 py-1 rounded-full text-xs font-semibold " + getStatusColor(selectedReport.status)}>{getStatusLabel(selectedReport.status)}</span></div>
              <div><p className="text-sm text-gray-500">{T.lblStartDate}</p><p className="font-semibold">{new Date(selectedReport.startDate).toLocaleDateString('ar-EG')}</p></div>
              <div><p className="text-sm text-gray-500">{T.lblEndDate}</p><p className="font-semibold">{new Date(selectedReport.endDate).toLocaleDateString('ar-EG')}</p></div>
              <div><p className="text-sm text-gray-500">{T.lblAmount}</p><p className="font-bold text-lg">{selectedReport.totalAmount.toLocaleString()} {selectedReport.currency}</p></div>
              <div><p className="text-sm text-gray-500">{T.lblPrevAmount}</p><p className="font-semibold">{selectedReport.previousPeriodAmount.toLocaleString()} {selectedReport.currency}</p></div>
              <div className="col-span-2"><p className="text-sm text-gray-500">{T.lblGrowth}</p>
                <span className={"inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold " + (selectedReport.growthPercentage >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700')}>
                  {selectedReport.growthPercentage >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {selectedReport.growthPercentage}%
                </span>
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button onClick={() => { setShowViewModal(false); openEdit(selectedReport); }} className="flex-1 bg-indigo-600 hover:bg-indigo-700"><Edit className="w-4 h-4 ml-2" />{T.edit}</Button>
              <Button onClick={() => setShowViewModal(false)} variant="outline" className="flex-1">{T.close}</Button>
            </div>
          </div></Card>
        </div>
      )}
    </div>
  );
}
